import type { ComponentProps } from "react";
import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { cn } from "cn";

/** Base UI accepts a class name or a function of the part's state; both have to survive merging. */
function merge<State>(
  base: string,
  className: string | ((state: State) => string | undefined) | undefined,
) {
  return (state: State) => cn(base, typeof className === "function" ? className(state) : className);
}

/**
 * The button that opens the menu. Deliberately unstyled beyond the slot: a menu is opened from a
 * workspace card here and could be opened from an icon elsewhere, and Base UI already supplies
 * `aria-haspopup`, `aria-expanded` and the focus return on close.
 */
function MenuTrigger({ className, ...props }: MenuPrimitive.Trigger.Props) {
  return (
    <MenuPrimitive.Trigger {...props} data-slot="menu-trigger" className={merge("", className)} />
  );
}

/**
 * The popup, anchored under the trigger's leading edge. Width is the caller's, because it is the
 * content that decides how wide a menu should be. Its portal sits above modal panels so a
 * workspace menu remains clickable inside the phone navigation drawer.
 */
function MenuContent({
  className,
  children,
  side = "bottom",
  align = "start",
  sideOffset = 8,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, "side" | "align" | "sideOffset">) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner className="z-60" side={side} align={align} sideOffset={sideOffset}>
        <MenuPrimitive.Popup
          {...props}
          data-slot="menu-content"
          className={merge(
            "max-h-(--available-height) max-w-[calc(100vw-2rem)] origin-(--transform-origin) overflow-y-auto rounded-[10px] border border-border bg-card p-1.25 text-foreground shadow-(--shadow-menu)",
            className,
          )}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

function MenuItem({ className, ...props }: MenuPrimitive.Item.Props) {
  return (
    <MenuPrimitive.Item
      {...props}
      data-slot="menu-item"
      className={merge(
        "flex h-7.5 cursor-pointer items-center gap-2 rounded-[6px] px-2 text-(length:--text-option) leading-(--text-option--line-height) outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-selected [&>svg]:size-3.5 [&>svg]:shrink-0",
        className,
      )}
    />
  );
}

/**
 * One choice of a {@link Menu.RadioGroup}, styled as an item with a check on the chosen one.
 * Choosing leaves the menu open, so a menu can hold more than one group to set in one visit.
 */
function MenuRadioItem({ className, children, ...props }: MenuPrimitive.RadioItem.Props) {
  return (
    <MenuPrimitive.RadioItem
      closeOnClick={false}
      {...props}
      data-slot="menu-radio-item"
      className={merge(
        "flex h-7.5 cursor-pointer items-center gap-2 rounded-[6px] px-2 text-(length:--text-option) leading-(--text-option--line-height) outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-selected [&>svg]:size-3.5 [&>svg]:shrink-0",
        className,
      )}
    >
      {children}
      <span aria-hidden="true" className="ml-auto grid size-4 shrink-0 place-items-center">
        <MenuPrimitive.RadioItemIndicator>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2.5 6.25 5 8.5l4.5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </MenuPrimitive.RadioItemIndicator>
      </span>
    </MenuPrimitive.RadioItem>
  );
}

/**
 * An on/off choice in a menu, styled as an item with a check while it is on. Like a radio item,
 * choosing leaves the menu open.
 */
function MenuCheckboxItem({ className, children, ...props }: MenuPrimitive.CheckboxItem.Props) {
  return (
    <MenuPrimitive.CheckboxItem
      closeOnClick={false}
      {...props}
      data-slot="menu-checkbox-item"
      className={merge(
        "flex h-7.5 cursor-pointer items-center gap-2 rounded-[6px] px-2 text-(length:--text-option) leading-(--text-option--line-height) outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-selected [&>svg]:size-3.5 [&>svg]:shrink-0",
        className,
      )}
    >
      {children}
      <span aria-hidden="true" className="ml-auto grid size-4 shrink-0 place-items-center">
        {/* Kept mounted so ticking and clearing can animate (motion.css), as in Select. */}
        <MenuPrimitive.CheckboxItemIndicator keepMounted data-slot="menu-checkbox-indicator">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              pathLength={1}
              d="M2.5 6.25 5 8.5l4.5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
    </MenuPrimitive.CheckboxItem>
  );
}

function MenuGroupLabel({ className, ...props }: MenuPrimitive.GroupLabel.Props) {
  return (
    <MenuPrimitive.GroupLabel
      {...props}
      data-slot="menu-group-label"
      className={merge(
        "px-2 pt-2.75 pb-1.5 font-mono text-[10px]/3 tracking-[0.08em] text-subtle-foreground",
        className,
      )}
    />
  );
}

function MenuSeparator({ className, ...props }: ComponentProps<typeof MenuPrimitive.Separator>) {
  return (
    <MenuPrimitive.Separator
      {...props}
      data-slot="menu-separator"
      className={merge("h-px shrink-0 bg-divider", className)}
    />
  );
}

/** Supported Menu parts only; Root keeps Base UI's own open-state and modality contract. */
export const Menu = {
  Root: MenuPrimitive.Root,
  Trigger: MenuTrigger,
  Content: MenuContent,
  Group: MenuPrimitive.Group,
  GroupLabel: MenuGroupLabel,
  Item: MenuItem,
  RadioGroup: MenuPrimitive.RadioGroup,
  RadioItem: MenuRadioItem,
  CheckboxItem: MenuCheckboxItem,
  Separator: MenuSeparator,
};
